import { useEffect, useRef } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { AppShell } from './components/AppShell';
import { useAuth } from './lib/auth';
import { resolveHomePagePath } from './lib/home-page';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { OrgSettingsPage } from './pages/OrgSettingsPage';
import { OrgDesignPage } from './pages/OrgDesignPage';
import { OrgAuthDesignPage } from './pages/OrgAuthDesignPage';
import { OrgCustomDomainPage } from './pages/OrgCustomDomainPage';
import { OrgGroupsPage } from './pages/OrgGroupsPage';
import { OrgSeasonsPage } from './pages/OrgSeasonsPage';
import { OrgNavigationPage } from './pages/OrgNavigationPage';
import { OrgUserManagementPage } from './pages/OrgUserManagementPage';
import { OrgMailPage } from './pages/OrgMailPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordPages';
import { InvitePage } from './pages/InvitePage';
import { ConfirmRegistrationPage } from './pages/ConfirmRegistrationPage';
import { PlatformOrgsPage } from './pages/PlatformOrgsPage';
import { PlatformAdminsPage } from './pages/PlatformAdminsPage';
import { PlatformClubAdminsPage } from './pages/PlatformClubAdminsPage';
import { PlatformUsersPage } from './pages/PlatformUsersPage';
import { PlatformSettingsPage } from './pages/PlatformSettingsPage';
import { PlatformMailPage } from './pages/PlatformMailPage';
import { PlatformDesignPage } from './pages/PlatformDesignPage';
import { MembersPage } from './pages/MembersPage';
import { MemberDetailPage } from './pages/MemberDetailPage';
import { TurnierDePage } from './pages/TurnierDePage';
import { TrainingPage } from './pages/TrainingPage';
import { TrainingEventPage } from './pages/TrainingEventPage';
import { SeasonPage } from './pages/SeasonPage';
import { CalendarPage } from './pages/CalendarPage';
import { EquipmentPage } from './pages/EquipmentPage';
import { OrgTrainersPage } from './pages/OrgTrainersPage';
import { TrainerCheckinPage } from './pages/TrainerCheckinPage';
import { ImprintPage } from './pages/ImprintPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { PlatformLegalPage } from './pages/PlatformLegalPage';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage });
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
});
const confirmRegistrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/confirm-registration/$token',
  component: ConfirmRegistrationPage,
});
// Öffentlich, ohne Login erreichbar (§ 5 DDG/Art. 13 DSGVO verlangen das) —
// deshalb an rootRoute statt appRoute gehängt, wie loginRoute.
const imprintRoute = createRoute({ getParentRoute: () => rootRoute, path: '/impressum', component: ImprintPage });
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/datenschutz', component: PrivacyPage });

function AuthedLayout() {
  const { me, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const active = me?.memberships.find((m) => m.organizationId === me.activeOrganizationId);
  // Ein Konto, das noch auf Freischaltung wartet (oder eingeladen/gesperrt ist), hat serverseitig
  // ohnehin keine Berechtigungen — aber ohne diese Sperre würde die Seite selbst trotzdem laden
  // (nur mit leeren/fehlerhaften Daten), erreichbar per direkter URL-Eingabe. Erzwingt statt
  // dessen sofort das Dashboard, das für diesen Status seine eigene Erklärkarte zeigt.
  const blocked = !loading && !!active && active.status !== 'active' && location.pathname !== '/';

  useEffect(() => {
    if (blocked) void navigate({ to: '/', replace: true });
  }, [blocked, navigate]);

  // Startseite (§ Nutzeranfrage "beim Öffnen der App soll die eingestellte
  // Seite erscheinen"): einmal je Anwendungsstart — Reload, neuer Tab,
  // PWA-Neustart — auf die gespeicherte Präferenz umleiten, sofern man
  // gerade auf "/" landet. `appliedRef` verhindert ein erneutes Umleiten bei
  // späterer, bewusster SPA-interner Navigation zurück zu "/" innerhalb
  // derselben Sitzung (`AuthedLayout` bleibt über Routenwechsel hinweg
  // gemountet, der Ref also gesetzt) — nur ein echtes Neuladen der Seite
  // mountet diese Komponente neu und setzt ihn zurück. Ohne dieses Gate
  // würde man ständig zur Präferenz zurückgeschickt, sobald man manuell zum
  // Dashboard navigiert (siehe docs/profile.md).
  const appliedHomePageRef = useRef(false);
  useEffect(() => {
    if (appliedHomePageRef.current || loading || !me || location.pathname !== '/') return;
    appliedHomePageRef.current = true;
    const target = resolveHomePagePath(me);
    if (target !== '/') void navigate({ to: target, replace: true });
  }, [loading, me, location.pathname, navigate]);

  if (loading) return <div className="p-8 text-sm text-slate-500">Lädt…</div>;
  if (!me) {
    window.location.assign('/login');
    return null;
  }
  if (blocked) return null;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AuthedLayout,
});

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage });
const orgRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization',
  component: OrgSettingsPage,
});
const orgDesignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/design',
  component: OrgDesignPage,
});
const orgAuthDesignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/auth-design',
  component: OrgAuthDesignPage,
});
const orgCustomDomainRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/custom-domain',
  component: OrgCustomDomainPage,
});
const orgGroupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/groups',
  component: OrgGroupsPage,
});
const orgSeasonsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/seasons',
  component: OrgSeasonsPage,
});
const orgNavigationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/navigation',
  component: OrgNavigationPage,
});
const orgUserManagementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/user-management',
  component: OrgUserManagementPage,
});
const orgMailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/mail',
  component: OrgMailPage,
});
const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/members',
  component: MembersPage,
});
const memberDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/members/$id',
  component: MemberDetailPage,
});
const trainingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/training',
  component: TrainingPage,
});
const trainingEventRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/training/events/$slug',
  component: TrainingEventPage,
});
const seasonRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/season',
  component: SeasonPage,
});
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendar',
  component: CalendarPage,
});
const equipmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/equipment',
  component: EquipmentPage,
});
const turnierDeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/turnier-de',
  component: TurnierDePage,
});
const orgTrainersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/organization/trainers',
  component: OrgTrainersPage,
});
const trainerCheckinRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/trainer',
  component: TrainerCheckinPage,
});
const platformOrgsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/organizations',
  component: PlatformOrgsPage,
});
const platformAdminsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/admins',
  component: PlatformAdminsPage,
});
const platformClubAdminsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/club-admins',
  component: PlatformClubAdminsPage,
});
const platformUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/users',
  component: PlatformUsersPage,
});
const platformSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/settings',
  component: PlatformSettingsPage,
});
const platformDesignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/design',
  component: PlatformDesignPage,
});
const platformMailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/mail',
  component: PlatformMailPage,
});
const platformLegalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/legal',
  component: PlatformLegalPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotRoute,
  resetRoute,
  inviteRoute,
  confirmRegistrationRoute,
  imprintRoute,
  privacyRoute,
  appRoute.addChildren([
    dashboardRoute,
    orgRoute,
    orgDesignRoute,
    orgAuthDesignRoute,
    orgCustomDomainRoute,
    orgGroupsRoute,
    orgSeasonsRoute,
    orgNavigationRoute,
    orgUserManagementRoute,
    orgMailRoute,
    membersRoute,
    memberDetailRoute,
    trainingRoute,
    trainingEventRoute,
    seasonRoute,
    calendarRoute,
    equipmentRoute,
    turnierDeRoute,
    orgTrainersRoute,
    trainerCheckinRoute,
    platformOrgsRoute,
    platformAdminsRoute,
    platformClubAdminsRoute,
    platformUsersRoute,
    platformDesignRoute,
    platformMailRoute,
    platformLegalRoute,
    platformSettingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
